import { createRootRouteWithContext, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { z } from "zod";
import type { AuthContextProps } from "react-oidc-context";
import { hasAuthParams } from "react-oidc-context";

import { getUserRoles } from "../shared/hooks/use-user-roles";
import { AppLayout } from "./app-layout";
import { CreateApplicationPage } from "../pages/create-application-page";
import { ApplicationDetailPage } from "../pages/application-detail-page";
import { ApplicationsPage } from "../pages/applications-page";
import { MonitoringPage } from "../pages/monitoring-page";
import { ConstructorServicesPage } from "../pages/constructor-services-page";
import { ConstructorRoadsPage } from "../pages/constructor-roads-page";
import { ConstructorDocumentsPage } from "../pages/constructor-documents-page";
import { ServiceDetailPage } from "../pages/service-detail-page";
import { ReferenceDocumentTypesPage } from "../pages/reference-document-types-page";
import { ProfilePage } from "../pages/profile-page";
import { UserInfoPage } from "../pages/user-info-page";

const DOCUMENT_TITLE_SUFFIX = " — EACD";

export type EacdRouterContext = {
  getAuth: () => AuthContextProps;
};

const AUTH_WAIT_MS = 120_000;

const ensureKeycloakSession = async (getAuth: () => AuthContextProps): Promise<void> => {
  const deadline = Date.now() + AUTH_WAIT_MS;

  while (Date.now() < deadline) {
    const auth = getAuth();

    if (auth.isAuthenticated && auth.user?.access_token) {
      return;
    }

    if (auth.error) {
      await auth.signinRedirect();
      await new Promise(() => {});
    }

    const busy = auth.isLoading || auth.activeNavigator || (hasAuthParams() && !auth.isAuthenticated);

    if (!busy && (!auth.isAuthenticated || !auth.user?.access_token)) {
      await auth.signinRedirect();
      await new Promise(() => {});
    }

    await new Promise((r) => setTimeout(r, 24));
  }

  throw new Error("Истекло время ожидания входа в Keycloak");
};

const rootRoute = createRootRouteWithContext<EacdRouterContext>()({
  head: () => ({
    meta: [{ title: "EACD" }],
  }),
  component: AppLayout,
  errorComponent: ({ error }) => {
    const message = error instanceof Error ? error.message : "Неизвестная ошибка";
    const stack = error instanceof Error ? error.stack : null;

    return (
      <div role="alert" className="text-type-error p-4">
        {message}
        {stack && <div className="text-type-minor mt-2 whitespace-pre-wrap">{stack}</div>}
      </div>
    );
  },
  pendingComponent: () => <div className="flex h-screen w-full items-center justify-center bg-[#F5F1F0] text-[#afafaf]">Вход…</div>,
  beforeLoad: async ({ context }) => {
    await ensureKeycloakSession(context.getAuth);
  },
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: z.looseObject({
    monitoringMetric: z.string().optional(),
    railway: z.string().optional(),
  }),
  beforeLoad: () => {
    // Внутренний контур, роль «ВНУТРЕННЕЕ СОГЛАСОВАНИЕ АДМИНИСТРАТОР УСЛУГ»:
    // корневой путь ведёт сразу в ЛК администратора услуг (конструктор).
    if (getUserRoles().isInternalServiceAdministrator) {
      throw redirect({ to: "/admin" });
    }
  },
  head: () => ({
    meta: [{ title: `Заявки${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: ApplicationsPage,
});

const createApplicationSearchSchema = z.object({
  draftId: z.uuid().optional(),
});

const createApplicationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/create",
  validateSearch: createApplicationSearchSchema,
  head: () => ({
    meta: [{ title: `Создание заявки${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: CreateApplicationPage,
});

const applicationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/$id",
  validateSearch: z.object({ tab: z.literal("final-decision").optional().catch(undefined) }),
  head: ({ params }) => ({
    meta: [{ title: `Заявка №${params.id}${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: ApplicationDetailPage,
});

const monitoringRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/monitoring",
  head: () => ({
    meta: [{ title: `Мониторинг${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: MonitoringPage,
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  head: () => ({
    meta: [{ title: `Профиль${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: ProfilePage,
});

const userInfoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/user-info",
  head: () => ({
    meta: [{ title: `Информация о пользователе${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: UserInfoPage,
});

/**
 * ЛК администратора услуг (внутренний контур, роль «ВНУТРЕННЕЕ СОГЛАСОВАНИЕ АДМИНИСТРАТОР УСЛУГ»).
 * Доступ к страницам ограничивается на уровне страниц (см. constructor-services-page).
 * Навбар для /admin/* переключается в AppLayout.
 */
const adminServicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  head: () => ({
    meta: [{ title: `Конструктор${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: ConstructorServicesPage,
});

const adminRoadsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/roads",
  head: () => ({
    meta: [{ title: `Конструктор — Дороги${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: ConstructorRoadsPage,
});

const adminDocumentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/documents",
  head: () => ({
    meta: [{ title: `Конструктор — Документы${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: ConstructorDocumentsPage,
});

const adminServiceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/services/$id",
  head: () => ({
    meta: [{ title: `Конструктор — Услуга${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: ServiceDetailPage,
});

const adminReferenceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/reference/document-types",
  head: () => ({
    meta: [{ title: `Справочники${DOCUMENT_TITLE_SUFFIX}` }],
  }),
  component: ReferenceDocumentTypesPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  createApplicationRoute,
  applicationRoute,
  monitoringRoute,
  profileRoute,
  userInfoRoute,
  adminServicesRoute,
  adminRoadsRoute,
  adminDocumentsRoute,
  adminServiceDetailRoute,
  adminReferenceRoute,
]);

export const router = createRouter({
  routeTree,
  context: {
    getAuth: () => {
      throw new Error("Router: getAuth ещё не передан через RouterProvider");
    },
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
